import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { upsertSectionRouting } from "./SectionAPI";

const POSTS_URL = "/heycallcenter/";

const initialState = {
  sections: [],
  status: "idle",
  error: null,
  hasFetched: false,
};

export const getCallFlow = createAsyncThunk(
  POSTS_URL + "api/CallFlow",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(POSTS_URL + "api/CallFlow", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const sec = [
        ...response.data.map((sec) => {
          const wd = JSON.parse(sec.workingDays);
          const msisdn = sec.msisdn ? sec.msisdn.split(",").filter(Boolean) : [];
          const ext    = sec.ext    ? sec.ext.split(",").filter(Boolean)    : [];
          // agents: [{ext, active}] from server — populate per-agent active map
          const agents      = sec.agents      || [];
          const ringMembers = sec.ringMembers || [];
          // Build lookup maps keyed by extension string
          const agentActives    = Object.fromEntries(agents.map(a      => [a.ext, a.active ?? 1]));
          const memberPriorities = Object.fromEntries(ringMembers.map(m => [m.ext, m.priority ?? 1]));
          const memberActives    = Object.fromEntries(ringMembers.map(m => [m.ext, m.active ?? 1]));
          return {
            ...sec,
            workingDays: wd,
            msisdn,
            ext,
            agents,
            ringMembers,
            agentActives,
            memberPriorities,
            memberActives,
            divertMethod:     sec.divertMethod     || 'QUEUE',
            queueStrategy:    sec.queueStrategy    || 'rrmemory',
            queueMaxWait:     sec.queueMaxWait     ?? 120,
            queueAttemptRing: sec.queueAttemptRing ?? 20,
            queueAnnounce:    sec.queueAnnounce    ?? 15,
            ringStrategy:     sec.ringStrategy     || 'ringall',
            ringTimeout:      sec.ringTimeout      ?? 20,
          };
        }),
      ];
      return sec;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const patchSection = createAsyncThunk(
  "sections/patch",
  async (sectionName, { getState }) => {
    const state   = getState();
    const section = state.sections.sections.find((s) => s.name === sectionName);
    if (!section?.dbId) return null;

    const profile = state.profile.profile;

    const msisdn = (section.msisdn || []).filter(Boolean);
    const ext    = (section.ext    || []).filter(Boolean);

    const routeType    = msisdn.length > 0 ? "GSM" : ext.length > 0 ? "SIP" : "Voice Mail";
    const divertMethod = routeType === 'SIP' ? (section.divertMethod || 'QUEUE') : null;

    // Route: GSM = number CSV, SIP = extension CSV (informational), VoiceMail literal
    const route =
      routeType === 'GSM'        ? msisdn.join(",") :
      routeType === 'SIP'        ? ext.join(",") :
      "Voice Mail";

    const workingDays = profile.isCustomWorkingDays ? section.workingDays : profile.workingDays;

    // Build per-agent and per-member arrays from the section maps
    const agentActives    = section.agentActives    || {};
    const memberPriorities = section.memberPriorities || {};
    const memberActives    = section.memberActives    || {};

    const agents = ext.map(e => ({ extension: e, active: agentActives[e] ?? 1 }));
    const ringMembers = ext.map(e => ({
      extension: e,
      priority: memberPriorities[e] ?? 1,
      active:   memberActives[e]   ?? 1,
    }));

    await upsertSectionRouting({
      CallFlowId:              section.dbId,
      SectionName:             section.name,
      RouteType:               routeType,
      DivertMethod:            divertMethod,
      Route:                   route,
      WorkingDaysAndTimesJson: JSON.stringify(workingDays || {}),
      Language:                profile.languages.join(","),
      // QUEUE / DIRECT
      Agents:           agents,
      QueueStrategy:    section.queueStrategy    ?? 'rrmemory',
      MaxWaitSec:       section.queueMaxWait      ?? 120,
      AttemptRingSec:   section.queueAttemptRing  ?? 20,
      AnnounceEverySec: section.queueAnnounce     ?? 15,
      // RING
      RingMembers:      ringMembers,
      RingStrategy:     section.ringStrategy ?? 'ringall',
      RingTimeout:      section.ringTimeout  ?? 20,
    });

    return sectionName;
  }
);

const sectionsSlice = createSlice({
  name: "sections",
  initialState,
  reducers: {
    addSections: (state, action) => {
      state.sections.push(action.payload);
    },
    removeSections: (state, action) => {
      const toRemove = Array.isArray(action.payload) ? action.payload : [action.payload];
      state.sections = state.sections.filter(
        (section) => !toRemove.includes(section.name)
      );
    },
    addmsisdn: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.id === action.payload.id
      );
      if (section) section.msisdn.push(action.payload.msisdn);
    },
    editmsisdn: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.id === action.payload.id
      );
      if (section) section.msisdn[action.payload.index] = action.payload.msisdn;
    },
    addExt: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.id === action.payload.id
      );

      if (section) section.ext.push(action.payload.ext);
    },
    editExt: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.id === action.payload.id
      );

      if (section) section.ext[action.payload.index] = action.payload.ext;
    },
    distroymsisdnArray: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.id === action.payload.id
      );
      if (section) section.msisdn = [];
    },
    distroyExtArray: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.id === action.payload.id
      );
      if (section) section.ext = [];
    },
    removemsisdn: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.id === action.payload.id
      );

      if (section)
        section.msisdn = section.msisdn.filter(
          (_, index) => index != action.payload.index
        );
    },
    removeExt: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.id === action.payload.id
      );

      if (section)
        section.ext = section.ext.filter(
          (_, index) => index != action.payload.index
        );
    },
    setWorkingDays: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.name === action.payload.name
      );
      if (section) section.workingDays = action.payload.workingDays;
    },
    setCopyOfWorkingDays: (state, action) => {
      state.sections.forEach(
        (sec) => (sec.workingDays = action.payload.workingDays)
      );
    },
    setRouteType: (state, action) => {
      const section = state.sections.find((sec) => sec.id === action.payload.id);
      if (section) section.routeType = action.payload.routeType;
    },
    setStrategy: (state, action) => {
      const section = state.sections.find((sec) => sec.id === action.payload.id);
      if (section) section.strategy = action.payload.strategy;
    },
    setDivertMethod: (state, action) => {
      const section = state.sections.find((sec) => sec.id === action.payload.id);
      if (section) section.divertMethod = action.payload.divertMethod;
    },
    setRingSettings: (state, action) => {
      const section = state.sections.find((sec) => sec.id === action.payload.id);
      if (!section) return;
      if (action.payload.ringStrategy !== undefined) section.ringStrategy = action.payload.ringStrategy;
      if (action.payload.ringTimeout  !== undefined) section.ringTimeout  = action.payload.ringTimeout;
      if (action.payload.ringPriority !== undefined) section.ringPriority = action.payload.ringPriority;
      if (action.payload.ringActive   !== undefined) section.ringActive   = action.payload.ringActive;
    },
    setQueueSettings: (state, action) => {
      const section = state.sections.find((sec) => sec.id === action.payload.id);
      if (!section) return;
      if (action.payload.queueStrategy    !== undefined) section.queueStrategy    = action.payload.queueStrategy;
      if (action.payload.queueMaxWait     !== undefined) section.queueMaxWait     = action.payload.queueMaxWait;
      if (action.payload.queueAttemptRing !== undefined) section.queueAttemptRing = action.payload.queueAttemptRing;
      if (action.payload.queueAnnounce    !== undefined) section.queueAnnounce    = action.payload.queueAnnounce;
    },
    // Per-agent active toggle (QUEUE / DIRECT)
    setAgentActive: (state, action) => {
      const section = state.sections.find((sec) => sec.id === action.payload.id);
      if (!section) return;
      if (!section.agentActives) section.agentActives = {};
      section.agentActives[action.payload.ext] = action.payload.active;
    },
    // Per-ring-member priority + active (RING)
    setMemberPriority: (state, action) => {
      const section = state.sections.find((sec) => sec.id === action.payload.id);
      if (!section) return;
      if (!section.memberPriorities) section.memberPriorities = {};
      section.memberPriorities[action.payload.ext] = action.payload.priority;
    },
    setMemberActive: (state, action) => {
      const section = state.sections.find((sec) => sec.id === action.payload.id);
      if (!section) return;
      if (!section.memberActives) section.memberActives = {};
      section.memberActives[action.payload.ext] = action.payload.active;
    },
    setPendingRouting: (state, action) => {
      const section = state.sections.find((s) => s.name === action.payload.name);
      if (!section) return;
      // Persist the form state so the dialog restores it on re-open
      section.pendingForm    = action.payload.form;
      // Persist the pre-built API payload for flush-on-deploy
      section.pendingPayload = action.payload.payload;
      // Update display fields so SwiperCard reflects the chosen routing
      const { form } = action.payload;
      section.routeType    = form.routeType;
      section.divertMethod = form.divertMethod;
      if (form.routeType === "GSM") {
        section.msisdn = form.externalNumber ? [form.externalNumber] : [];
        section.ext    = [];
      } else if (form.routeType === "SIP") {
        section.msisdn = [];
        const exts = form.divertMethod === "RING"
          ? (form.ringMembers || []).filter((m) => m.extension?.trim()).map((m) => m.extension.trim())
          : (form.agents     || []).filter((a) => a.extension?.trim()).map((a) => a.extension.trim());
        section.ext = exts;
      } else {
        section.msisdn = [];
        section.ext    = [];
      }
    },
    setWorkingHour: (state, action) => {
      const section = state.sections.find(
        (sec) => sec.name === action.payload.name
      );
      if (section) {
        if (action.payload.pType === "from") {
          section.workingDays[action.payload.day].from = action.payload.value;
        } else {
          section.workingDays[action.payload.day].to = action.payload.value;
        }
      }
    },
    // Replace the entire sections list from a parsed FlowJson (active FlowDefinition).
    // Used when card view loads after visual builder has deployed a flow.
    setSectionsFromFlow: (state, action) => {
      state.sections   = action.payload;
      state.status     = "Success";
      state.hasFetched = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCallFlow.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCallFlow.fulfilled, (state, action) => {
        state.status = "Success";
        state.sections = action.payload;
        state.hasFetched = true;
      })
      .addCase(getCallFlow.rejected, (state, action) => {
        state.status = "Rejected";
      });
  },
});

export const {
  addSections,
  removeSections,
  setSectionsFromFlow,
  setPendingRouting,
  addmsisdn,
  addExt,
  editmsisdn,
  editExt,
  distroymsisdnArray,
  distroyExtArray,
  removemsisdn,
  removeExt,
  setRouteType,
  setStrategy,
  setDivertMethod,
  setRingSettings,
  setQueueSettings,
  setAgentActive,
  setMemberPriority,
  setMemberActive,
  setWorkingDays,
  setCopyOfWorkingDays,
  setWorkingHour,
} = sectionsSlice.actions;
export const sectionsStatus = (state) => state.sections.status;
export default sectionsSlice.reducer;
